import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DICTIONARY } from './dictionary';

const STORAGE_KEY = 'oing-lang';

const LanguageContext = createContext({
  lang: 'en',
  setLang: () => {},
});

/**
 * 전역 언어 상태 (en | kr).
 * - localStorage('oing-lang')에 유지
 * - <html lang> 자동 반영
 * GNB LangSelector가 setLang을 호출하고, BaseButton은 lang을 읽어
 * KR 타이포그래피로 전환한다.
 */
export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window === 'undefined') return 'en';
    try {
      return window.localStorage.getItem(STORAGE_KEY) === 'kr' ? 'kr' : 'en';
    } catch {
      return 'en';
    }
  });

  const setLang = useCallback((next) => {
    const value = next === 'kr' || next === 'KR' ? 'kr' : 'en';
    setLangState(value);
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* private mode 등 저장 실패 무시 */
    }
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang === 'kr' ? 'ko' : 'en';
    }
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang }), [lang, setLang]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);

/**
 * 번역 훅.
 * @example
 * const t = useT();
 * t('Job Board')            // 사전 조회 → '채용 공고' (KR일 때)
 * t('Hello', '안녕하세요')     // 명시적 페어
 */
export const useT = () => {
  const { lang } = useLang();
  return useCallback(
    (en, kr) => {
      if (lang !== 'kr') return en;
      if (kr != null) return kr;
      return DICTIONARY[en] ?? en;
    },
    [lang],
  );
};
